import {
  restaurantOperationalStatusComboboxOptions,
  restaurantTypes,
} from "@plasma/data/restaurants";
import { getAllStatesAsComboboxItems } from "@plasma/data/states";
import { usePhoneNumberMask } from "@plasma/utils/phone-numbers";
import { Coffee, Utensils } from "lucide-react";
import type {
  Control,
  FieldPath,
  FieldValues,
  UseFormResetField,
  UseFormWatch,
} from "react-hook-form";

import { LogoUploader } from "~/components/locations/logo-uploader";
import { Combobox } from "~/components/ui/combobox";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";

import { RadioPanels } from "../shared/radio-panels";
import { DateInput } from "../ui/date-input";
import {
  LocationBusinessHoursFields,
  type LocationBusinessHoursForm,
} from "./location-business-hours-fields";

function isRestaurantStyle(
  value: unknown
): value is keyof typeof restaurantTypes {
  return value === "QSR" || value === "FSR";
}

export interface Location {
  logoUrl: string | null;
  name: string;
  type: string;
  style: "QSR" | "FSR";
  address: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
  };
  phoneNumber: string;
  operationalStatus: string;
  closeDateRange: {
    closeStartDate: string | null;
    closeEndDate: string | null;
  };
  businessHours: {
    dayOfTheWeek: string;
    openTime: string | null;
    closeTime: string | null;
  }[];
}

type LocationFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  name: TName;
};

export const LocationLogoField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  setUploading,
}: LocationFieldProps<TFieldValues, TName> & {
  setUploading: (uploading: boolean) => void;
}) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="items-center">
          <FormControl className="items-center">
            <LogoUploader
              onValueChange={(url) => {
                field.onChange(url);
              }}
              placeholderSrc="/images/location-logo-placeholder.svg"
              setUploading={setUploading}
              value={field.value ?? null}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const LocationNameField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
}: LocationFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Location name</FormLabel>
          <FormControl>
            <Input autoComplete="organization" {...field} maxLength={100} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
      rules={{ required: "Location name is required" }}
    />
  );
};

export const LocationAddressField = <TFieldValues extends FieldValues>({
  control,
  addressLine1Field,
  addressLine2Field,
  cityField,
  stateField,
  zipCodeField,
  resetField,
}: {
  control: Control<TFieldValues>;
  addressLine1Field: FieldPath<TFieldValues>;
  addressLine2Field: FieldPath<TFieldValues>;
  cityField: FieldPath<TFieldValues>;
  stateField: FieldPath<TFieldValues>;
  zipCodeField: FieldPath<TFieldValues>;
  resetField: UseFormResetField<TFieldValues>;
}) => {
  return (
    <>
      <div className="space-y-2">
        <p className="text-foreground text-weight-medium">Location address</p>
      </div>
      <FormField
        control={control}
        name={addressLine1Field}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Address line 1</FormLabel>
            <FormControl>
              <Input {...field} autoComplete="address-line1" maxLength={100} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={addressLine2Field}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Address line 2</FormLabel>
            <FormControl>
              <Input
                {...field}
                autoComplete="address-line2"
                maxLength={100}
                value={field.value ?? ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid grid-cols-3 gap-3">
        <FormField
          control={control}
          name={cityField}
          render={({ field }) => (
            <FormItem>
              <FormLabel>City</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  autoComplete="address-level2"
                  maxLength={50}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={stateField}
          render={({ field }) => (
            <FormItem>
              <FormLabel>State</FormLabel>
              <FormControl>
                <Combobox
                  items={getAllStatesAsComboboxItems()}
                  onValueChange={(state) => {
                    if (state) {
                      field.onChange(state);
                    } else {
                      resetField(stateField);
                    }
                  }}
                  value={field.value ?? null}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={zipCodeField}
          render={({ field }) => (
            <FormItem>
              <FormLabel>ZIP</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  autoComplete="postal-code"
                  inputMode="numeric"
                  maxLength={5}
                  minLength={5}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <FormItem>
        <FormLabel>Country</FormLabel>
        <FormControl>
          <Input defaultValue="United States" disabled />
        </FormControl>
      </FormItem>
    </>
  );
};

export const LocationPhoneNumberField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
}: LocationFieldProps<TFieldValues, TName>) => {
  const inputMaskRef = usePhoneNumberMask();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Phone number</FormLabel>
          <FormControl>
            <Input
              autoComplete="tel"
              inputMode="tel"
              name={field.name}
              onBlur={field.onBlur}
              onChange={field.onChange}
              placeholder="+1 (903) 395-9401"
              ref={inputMaskRef}
              type="text"
              value={typeof field.value === "string" ? field.value : ""}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const LocationStyleAndTypeField = <TFieldValues extends FieldValues>({
  control,
  styleField,
  typeField,
  watch,
  resetField,
}: {
  control: Control<TFieldValues>;
  styleField: FieldPath<TFieldValues>;
  typeField: FieldPath<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
  resetField: UseFormResetField<TFieldValues>;
}) => {
  const style = watch(styleField);

  return (
    <>
      <FormField
        control={control}
        name={styleField}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Location style</FormLabel>
            <FormControl>
              <RadioPanels<"QSR" | "FSR">
                items={[
                  {
                    value: "QSR",
                    label: "QSR",
                    description: "Fast food, take-out and delivery",
                    icon: Coffee,
                  },
                  {
                    value: "FSR",
                    label: "FSR",
                    description: "Dine-in, table service",
                    icon: Utensils,
                  },
                ]}
                onValueChange={(val) => {
                  field.onChange(val);
                  resetField(typeField);
                }}
                value={field.value ?? "QSR"}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={typeField}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Location type</FormLabel>
            <FormControl>
              <Select
                disabled={!style}
                onValueChange={field.onChange}
                value={field.value}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status..." />
                </SelectTrigger>
                <SelectContent>
                  {isRestaurantStyle(style) &&
                    restaurantTypes[style].map((type: string) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};

export const LocationBusinessHoursField = <
  TFieldValues extends LocationBusinessHoursForm & FieldValues,
>({
  control,
  operationalStatusField,
  closeStartDateField,
  closeEndDateField,
  watch,
  resetField,
}: {
  control: Control<TFieldValues>;
  operationalStatusField: FieldPath<TFieldValues>;
  closeStartDateField: FieldPath<TFieldValues>;
  closeEndDateField: FieldPath<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
  resetField?: UseFormResetField<TFieldValues>;
}) => {
  const operationalStatus = watch(operationalStatusField);

  return (
    <div className="w-full space-y-5">
      <FormField
        control={control}
        name={operationalStatusField}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Operational status</FormLabel>
            <FormControl>
              <Select
                defaultValue="OPEN"
                onValueChange={(value) => {
                  if (value !== "TEMPORARILY_CLOSED") {
                    resetField?.(closeEndDateField);
                    resetField?.(closeStartDateField);
                  }
                  field.onChange(value);
                }}
                value={
                  typeof field.value === "string" ? field.value : undefined
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location type..." />
                </SelectTrigger>
                <SelectContent>
                  {restaurantOperationalStatusComboboxOptions.map(
                    ({ value, label }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {operationalStatus === "TEMPORARILY_CLOSED" && (
        <div className="flex flex-col gap-2">
          <p className="label text-foreground">Closure period (optional)</p>
          <div className="flex flex-row gap-3">
            <DateInput
              name={closeStartDateField}
              placeholder="Select start date"
            />
            <DateInput name={closeEndDateField} placeholder="Select end date" />
          </div>
        </div>
      )}
      {operationalStatus !== "PERMANENTLY_CLOSED" && (
        <LocationBusinessHoursFields showLabel />
      )}
    </div>
  );
};
